import React from 'react';
import LyftIcon from '../images/LyftIcon.png';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400;1,500&family=DM+Sans:wght@300;400;500&display=swap');

  :root {
    --ink:      #1A1814;
    --ink-2:    #4A4740;
    --ink-3:    #8A8780;
    --rule:     rgba(26,24,20,0.10);
    --rule-md:  rgba(26,24,20,0.18);
    --sand:     #F7F4EE;
    --white:    #FFFFFF;
    --pink:     #E5007A;
    --pink-lt:  #FEF0F7;
    --serif:    'Cormorant Garamond', Georgia, serif;
    --sans:     'DM Sans', system-ui, sans-serif;
  }

  /* ── HERO ─────────────────────────────────────────────────────────────────── */
  .ly-hero {
    background: var(--pink);
    padding: 80px 48px 72px;
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .ly-hero-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    object-fit: contain;
    background: rgba(255,255,255,0.15);
    padding: 6px;
    flex-shrink: 0;
  }

  .ly-hero-title {
    font-family: var(--serif);
    font-size: 56px;
    font-weight: 400;
    color: var(--white);
    line-height: 1;
  }

  /* ── BODY ─────────────────────────────────────────────────────────────────── */
  .ly-body {
    max-width: 800px;
    margin: 0 auto;
    padding: 64px 48px 80px;
  }

  .ly-body p {
    font-family: var(--sans);
    font-size: 17px;
    font-weight: 400;
    line-height: 1.85;
    color: var(--ink);
    margin-bottom: 28px;
  }

  .ly-body p:last-child { margin-bottom: 0; }

  /* ── PROMPT BLOCK ─────────────────────────────────────────────────────────── */
  .ly-prompt {
    border-left: 2px solid var(--pink);
    background: var(--pink-lt);
    border-radius: 0 4px 4px 0;
    padding: 24px 28px;
    margin: 0 0 36px;
  }

  .ly-prompt-label {
    font-family: var(--sans);
    font-size: 10px;
    font-weight: 400;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--pink);
    margin-bottom: 12px;
  }

  .ly-prompt p {
    font-family: var(--sans);
    font-size: 14px;
    font-weight: 300;
    line-height: 1.75;
    color: var(--ink-2);
    margin-bottom: 10px;
  }

  .ly-prompt p:last-child { margin-bottom: 0; }

  /* ── SECTION LABEL ────────────────────────────────────────────────────────── */
  .ly-section-label {
    font-family: var(--sans);
    font-size: 10px;
    font-weight: 400;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--ink-3);
    margin-bottom: 16px;
    padding-top: 8px;
    border-top: 0.5px solid var(--rule-md);
  }

  /* ── FORMULA BLOCK ────────────────────────────────────────────────────────── */
  .ly-formula {
    background: var(--sand);
    border: 0.5px solid var(--rule-md);
    border-radius: 4px;
    padding: 20px 24px;
    margin: 0 0 28px;
    font-family: var(--sans);
    font-size: 13px;
    font-weight: 300;
    color: var(--ink-2);
    line-height: 1.8;
  }

  .ly-formula-eq {
    font-family: 'Courier New', monospace;
    font-size: 13px;
    color: var(--ink);
    display: block;
    margin: 6px 0;
  }

  .ly-formula-result {
    font-weight: 500;
    color: var(--pink);
  }

  /* ── COMPARISON TABLE ─────────────────────────────────────────────────────── */
  .ly-table-wrap {
    overflow-x: auto;
    margin: 0 0 36px;
    border: 0.5px solid var(--rule-md);
    border-radius: 4px;
  }

  .ly-table {
    width: 100%;
    border-collapse: collapse;
    font-family: var(--sans);
    font-size: 13px;
  }

  .ly-table th {
    background: var(--sand);
    font-weight: 500;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink-3);
    padding: 12px 16px;
    text-align: left;
    border-bottom: 0.5px solid var(--rule-md);
  }

  .ly-table th:not(:first-child) {
    text-align: right;
    border-left: 0.5px solid var(--rule);
  }

  .ly-table td {
    padding: 11px 16px;
    color: var(--ink-2);
    font-weight: 300;
    border-bottom: 0.5px solid var(--rule);
  }

  .ly-table td:not(:first-child) {
    text-align: right;
    border-left: 0.5px solid var(--rule);
  }

  .ly-table tr:last-child td { border-bottom: none; }

  .ly-table tr.ly-table-highlight td {
    background: var(--pink-lt);
    color: var(--ink);
    font-weight: 400;
  }

  .ly-table tr.ly-table-highlight td:first-child {
    border-left: 2px solid var(--pink);
  }

  .ly-val-positive { color: var(--pink); font-weight: 500; }

  /* ── CAVEATS ──────────────────────────────────────────────────────────────── */
  .ly-caveats {
    border-top: 0.5px solid var(--rule-md);
    margin-top: 8px;
    padding-top: 28px;
  }

  .ly-caveat {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;
    font-family: var(--sans);
    font-size: 14px;
    font-weight: 300;
    line-height: 1.7;
    color: var(--ink-2);
  }

  .ly-caveat:last-child { margin-bottom: 0; }

  .ly-caveat-num {
    font-family: var(--serif);
    font-size: 22px;
    font-weight: 400;
    color: var(--ink-3);
    line-height: 1;
    flex-shrink: 0;
    width: 24px;
  }

  /* ── RESPONSIVE ──────────────────────────────────────────────────────────── */
  @media (max-width: 900px) {
    .ly-hero { padding: 56px 24px 48px; }
    .ly-hero-title { font-size: 40px; }
    .ly-body { padding: 48px 24px 64px; }
  }
`;

function LyftPage() {
  return (
    <div>
      <style>{styles}</style>

      {/* ── HERO ── */}
      <div className="ly-hero">
        <img src={LyftIcon} alt="Lyft" className="ly-hero-icon" />
        <span className="ly-hero-title">Lyft Pricing</span>
      </div>

      {/* ── BODY ── */}
      <div className="ly-body">

        <p>This is a pricing case study for Lyft&apos;s ride-scheduling feature launching in a new market. The goal: recommend a take rate that maximizes net revenue over 12 months on a single route — airport to downtown Toledo, Ohio.</p>

        <p>The constraints are fixed. Riders pay $25. At the starting take rate of $6 per ride, only 60% of requests are finding a driver. One experiment has been run: dropping the take to $3 raised the match rate to 93%. The question is what to do with that information.</p>

        {/* ── STARTING CONDITIONS ── */}
        <div className="ly-prompt">
          <div className="ly-prompt-label">Initial assumptions / guidelines</div>
          <p>Rate charged to riders: $25 (fixed)</p>
          <p>Driver payment at $6 take: $19 &nbsp;|&nbsp; Match rate: 60% &nbsp;|&nbsp; Driver CAC: $400–$600 &nbsp;|&nbsp; Driver churn: 5%/month &nbsp;|&nbsp; Rides/month: 100</p>
          <p>Driver payment at $3 take: $22 &nbsp;|&nbsp; Match rate: 93%</p>
          <p>Rider CAC: $10–$20 &nbsp;|&nbsp; Rider churn (matched): 10%/month &nbsp;|&nbsp; Rider churn (no match): 33%/month &nbsp;|&nbsp; Rides requested: 1/month</p>
        </div>

        {/* ── PROPOSAL ── */}
        <div className="ly-section-label">Proposal</div>

        <p>Given the ride matching results and churn for ride scheduling in Toledo for both the revenue per ride prices tested ($6 and $3), to maximize the revenue for the next 12 months, it is recommended to increase the revenue take to $3.50 per ride (assuming having to deploy a fixed price). By slightly increasing the revenue per ride, Lyft would be able to have a higher revenue upside, while also gathering additional, useful data on how price changes affect match rates. Keeping the take rate on the lower end would also keep match rates high, satisfying both riders and drivers.</p>

        {/* ── REVENUE PER REQUEST ── */}
        <div className="ly-section-label">Revenue per request</div>

        <p>Given the fixed driver churn rate, the focus shifts to rider churn, which is influenced by pricing. The 12-month net revenue was estimated for both pricing models by considering match rates and churn.</p>

        <div className="ly-formula">
          <span className="ly-formula-eq">Original ($6 revenue per ride): 0.6 rides per request (60% match rate) × $6 per ride = <span className="ly-formula-result">$3.60 / request</span></span>
          <span className="ly-formula-eq">Experimental ($3 revenue per ride): 0.93 rides per request (93% match rate) × $3 per ride = <span className="ly-formula-result">$2.79 / request</span></span>
        </div>

        {/* ── RIDER RETENTION ── */}
        <div className="ly-section-label">Rider retention</div>

        <div className="ly-formula">
          <span className="ly-formula-eq">Original ($6): (33% churn × 40 unmatched) + (10% churn × 60 matched) = <span className="ly-formula-result">19.2 riders lost per 100 requests → 80.8% retention</span></span>
          <span className="ly-formula-eq">Experimental ($3): (33% churn × 7 unmatched) + (10% churn × 93 matched) = <span className="ly-formula-result">11.61 riders lost per 100 requests → 88.39% retention</span></span>
        </div>

        {/* ── CLV ── */}
        <div className="ly-section-label">Customer lifetime value (12 months)</div>

        <p>Since revenue is directly affected by ride requests and ride requests go down with rider churn, revenue over the next 12 months can be calculated using a geometric series where S is the revenue, a is the revenue per rider/request, r is the retention rate, and n is the number of months:</p>

        <div className="ly-formula">
          <span className="ly-formula-eq">S = a × [(1 − r&#x207F;) / (1 − r)]</span>
          <span className="ly-formula-eq">Original ($6): 3.60 × [(1 − 0.808¹²) / (1 − 0.808)] = <span className="ly-formula-result">$17.30 CLV</span></span>
          <span className="ly-formula-eq">Experimental ($3): 2.79 × [(1 − 0.8839¹²) / (1 − 0.8839)] = <span className="ly-formula-result">$18.57 CLV</span></span>
        </div>

        {/* ── CLV WITH CAC TABLE ── */}
        <div className="ly-section-label">Net revenue per rider with CAC</div>

        <div className="ly-table-wrap">
          <table className="ly-table">
            <thead>
              <tr>
                <th>CAC</th>
                <th>$6 revenue per rider</th>
                <th>$3 revenue per rider</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>$10</td>
                <td>$7.30</td>
                <td>$8.57</td>
              </tr>
              <tr>
                <td>$15</td>
                <td>$2.30</td>
                <td>$3.57</td>
              </tr>
              <tr>
                <td>$20</td>
                <td>–$2.70</td>
                <td>–$1.43</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── THEORIZED TAKE RATE ── */}
        <div className="ly-section-label">Theorized take rate</div>

        <p>Although we only have limited data, it is hypothesized that increasing the take rate slightly would not lead to as drastic of a drop in match rate as we saw for the $6 price point — i.e. the relationship between the take rate and the match rate is not linear. This may be especially true with only a $0.50 price point increase.</p>

        <p>Estimating that the match rate decreases to 88%, a 5% drop from the $3 take rate, we can calculate the revenue per rider once again for a $3.50 take rate:</p>

        <div className="ly-formula">
          <span className="ly-formula-eq">$3.50 take: 0.88 rides per request (88% match rate) × $3.50 per ride = <span className="ly-formula-result">$3.08 / request</span></span>
          <span className="ly-formula-eq">Churn: (33% × 12 unmatched) + (10% × 88 matched) = 12.76 riders lost per 100 requests → <span className="ly-formula-result">87.24% retention</span></span>
          <span className="ly-formula-eq">CLV: 3.08 × [(1 − 0.8724¹²) / (1 − 0.8724)] = <span className="ly-formula-result">$19.45 CLV</span></span>
        </div>

        {/* ── FULL COMPARISON TABLE ── */}
        <div className="ly-table-wrap">
          <table className="ly-table">
            <thead>
              <tr>
                <th>CAC</th>
                <th>$3.50 revenue per rider</th>
                <th>$3 revenue per rider</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>$10</td>
                <td className="ly-val-positive">$9.45</td>
                <td>$8.57</td>
              </tr>
              <tr>
                <td>$15</td>
                <td className="ly-val-positive">$4.45</td>
                <td>$3.57</td>
              </tr>
              <tr>
                <td>$20</td>
                <td>–$0.55</td>
                <td>–$1.43</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── CONCLUSION ── */}
        <div className="ly-section-label">Conclusion</div>

        <p>The analysis demonstrates that reducing Lyft&apos;s take to $3 per ride increases revenue primarily due to reduced churn. However, increasing the take to $3.50 might offer even greater revenue with minimal impact on churn, indicating a non-linear relationship between pricing and match rates. In practice this means paying drivers $21.50 per ride instead of $22, keeping match rates high enough that rider churn stays low and long-term retention compounds in Lyft's favor.</p>

        {/* ── ADDITIONAL CONSIDERATIONS ── */}
        <div className="ly-section-label">Additional considerations</div>

        <div className="ly-caveats">
          <div className="ly-caveat">
            <span className="ly-caveat-num">1</span>
            <span>Long-Term CLV: This analysis focuses on a 12-month period; long-term customer lifetime value (CLV) might differ, especially as not all riders churn within this timeframe.</span>
          </div>
          <div className="ly-caveat">
            <span className="ly-caveat-num">2</span>
            <span>Driver-Rider Ratio: As riders churn, the driver-rider ratio could improve, potentially affecting match rates positively. This factor requires more data for a comprehensive model.</span>
          </div>
          <div className="ly-caveat">
            <span className="ly-caveat-num">3</span>
            <span>Driver Churn: While driver churn was considered constant, a lower take rate might improve driver satisfaction and reduce churn.</span>
          </div>
          <div className="ly-caveat">
            <span className="ly-caveat-num">4</span>
            <span>Variable CAC: CAC could rise with increased acquisition efforts. The financial impact of CAC should be monitored closely as part of ongoing pricing strategy adjustments.</span>
          </div>
        </div>

      </div>
    </div>
  );
}

export default LyftPage;
